import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { NuggetLink } from "@/types/nugget";
import type {
  DeckDocument,
  DeckLogo,
  GriddStyleSheetData,
  SlideData,
  SlideContents,
  SlideLayout,
  SlideLevel,
  SlideTransition,
} from "@/types/document";
import { createEmptyDeck, makeSlide } from "@/lib/deckFactory";
import { clampLogoSize } from "@/lib/deck-logo";
import {
  archivedEvidenceAfter,
  DEPTH_STORYLINE,
  depth,
  evidenceChildIds,
  levelOf,
  movePyramid,
  normalizePyramid,
} from "@/lib/pyramid";
import { clampDuration, DEFAULT_TRANSITION } from "@/lib/slide-transition";

const documentSlice = createSlice({
  name: "document",
  // A placeholder deck, replaced by `hydrated` as soon as the route's deck loads (§12.2).
  // Since §12 the store no longer *owns* the deck it starts with — which deck is open is a
  // property of the page's URL, not of the store — so this initial value is never persisted;
  // it just keeps the reducers typed and non-null before the first hydration.
  initialState: createEmptyDeck,
  reducers: {
    // Replaces the whole document on IndexedDB hydration (TODO §4.4).
    hydrated(_state, action: PayloadAction<DeckDocument>) {
      return action.payload;
    },
    setTitle(state, action: PayloadAction<string>) {
      state.title = action.payload;
    },
    // Commits an edited stylesheet (the promotion half of a Save — TODO §10.7).
    // Only committed sheets land here; drafts live in styleDraftSlice.
    setStyleSheet(state, action: PayloadAction<GriddStyleSheetData>) {
      state.styleSheet = action.payload;
    },
    // §10.3.4 Merge a partial change into the deck-level transition (the picker updates one
    // field at a time). `durationMs` is clamped here so an out-of-range value can never be
    // stored. Defends against a base that predates schemaVersion 5.
    setTransition(state, action: PayloadAction<Partial<SlideTransition>>) {
      const base = state.transition ?? DEFAULT_TRANSITION;
      const next = { ...base, ...action.payload };
      if (action.payload.durationMs !== undefined) {
        next.durationMs = clampDuration(next.durationMs);
      }
      state.transition = next;
    },
    // id is generated in the prepare callback so the reducer stays pure — a bare
    // crypto.randomUUID() inside the reducer would break time-travel debugging.
    //
    // §18.2.2 `afterId` inserts the new slide directly behind that slide instead of
    // appending; omitting it keeps the original append-to-the-end behaviour, so every
    // existing `addSlide()` call site is unchanged. The insert is an index splice on the
    // one flat `slides` array — the same array `moveSlide` reorders — so nothing else in
    // the app has to learn about position.
    //
    // An unknown `afterId` appends rather than throwing: the only way to reach it is a
    // slide deleted between render and click, and appending is the harmless reading of
    // "add a slide". Archived slides live in the same array but are filtered out for
    // display, so splicing beside an active slide is always correct in the active order
    // whatever archived rows sit around it.
    //
    // §18.2.2 `level` is the other half, and it arrived with the pyramid: "add an evidence
    // slide under this one" is one dispatch rather than an add followed by a patch, so the
    // deck is never briefly holding a storyline slide in the middle of an evidence block —
    // which `normalizePyramid` would have been entitled to act on. `title` is here for the
    // same reason: §18.2.8's backup divider is an ordinary slide called *Backups*, and a
    // second dispatch to name it would be a second write and a visible blank frame.
    //
    // §19.1.2 adds the other two options. `atStart` is what "Add cover" needs and `afterId`
    // cannot express — a cover goes *before* the first slide, and there is nothing to sit
    // after. `layout` lets that be ONE dispatch: adding the slide and then patching it to a
    // cover would put a non-cover slide in the deck for a render, which the numbering
    // helper and the logo overlay would both briefly believe.
    addSlide: {
      reducer(
        state,
        action: PayloadAction<{
          id: string;
          afterId?: string;
          atStart?: boolean;
          layout?: SlideLayout;
          level?: SlideLevel;
          title?: string;
        }>,
      ) {
        const slide = makeSlide(action.payload.id);
        const { afterId, atStart, layout, level, title } = action.payload;
        if (layout) slide.layout = layout;
        // Absent means storyline (§18.1.1), so the default level is written as no field at
        // all — a fresh deck's records stay exactly the shape they were before the pyramid.
        if (level && depth(level) !== DEPTH_STORYLINE) slide.level = level;
        if (title) slide.title = title;
        if (atStart) {
          state.slides.unshift(slide);
        } else {
          const at = afterId
            ? state.slides.findIndex((s) => s.id === afterId)
            : -1;
          if (at === -1) state.slides.push(slide);
          else state.slides.splice(at + 1, 0, slide);
        }
        state.slides = normalizePyramid(state.slides);
      },
      prepare(options?: {
        afterId?: string;
        atStart?: boolean;
        layout?: SlideLayout;
        level?: SlideLevel;
        title?: string;
      }) {
        return {
          payload: {
            id: crypto.randomUUID(),
            afterId: options?.afterId,
            atStart: options?.atStart,
            layout: options?.layout,
            level: options?.level,
            title: options?.title,
          },
        };
      },
    },
    updateSlide(
      state,
      action: PayloadAction<{ id: string; patch: Partial<SlideData> }>,
    ) {
      const slide = state.slides.find((s) => s.id === action.payload.id);
      if (slide) Object.assign(slide, action.payload.patch);
    },
    // §18.2.1 The pyramid's one new mutation. Pure: it applies the change and then runs
    // `normalizePyramid` over the array, which is the single place the two ordering
    // invariants are stated (§18.1.3).
    //
    // Promoting an evidence slide to storyline makes the evidence slides following it *its*
    // children. That is exactly right — the slide it used to sit under is no longer making a
    // claim those slides support — but it is a change the user did not type, so the caller
    // says so on screen rather than letting the outline silently re-draw.
    //
    // Demoting the FIRST storyline slide is **refused, not silently corrected**: normalize
    // would promote whatever ended up in front of it and the click would appear to have done
    // something else entirely. A deck's first slide is its first claim.
    setSlideLevel(
      state,
      action: PayloadAction<{ id: string; level: SlideLevel }>,
    ) {
      const { id, level } = action.payload;
      const slide = state.slides.find((s) => s.id === id);
      if (!slide || slide.archived || levelOf(slide) === level) return;
      const firstActive = state.slides.find((s) => !s.archived);
      if (firstActive?.id === id && depth(level) !== DEPTH_STORYLINE) return;
      // Absent = storyline (§18.1.1): the default is written as no field, never as the
      // string, so a deck that never uses the pyramid stores nothing new.
      if (depth(level) === DEPTH_STORYLINE) delete slide.level;
      else slide.level = level;
      state.slides = normalizePyramid(state.slides);
    },
    // === Knowledge nugget links (TODO §17) ===
    //
    // Four reducers, and every one of them is pure in the strict sense the rest of this file
    // is: no `crypto.randomUUID()` outside a `prepare`, no `new Date()`, and — the one that
    // matters here — **no `rev`**. A nugget's revision counter is owned by the storage layer
    // (§17.1.1), exactly like `DeckMeta.rev`, so it always ARRIVES in a payload having been
    // stamped inside an IndexedDB transaction. A reducer that incremented it would be two
    // pages both believing they produced revision 7.

    // §17.4.4 The slide becomes the first user of the nugget it just created; §17.4.5 uses it
    // for the second and every later user too. The caller has already copied (or written) the
    // bodies under the nugget's owner key — this only records the pointer.
    linkSet(state, action: PayloadAction<{ id: string; link: NuggetLink }>) {
      const slide = state.slides.find((s) => s.id === action.payload.id);
      if (slide) slide.link = action.payload.link;
    },
    // §17.4.3 Break link. The slide keeps every one of its materialized fields and simply
    // ceases to be shared, pointed at the private copies the caller has just written.
    //
    // **Repointing and unlinking are ONE action, and they have to be.** Split into an
    // `updateSlide` and a separate unlink, either order is wrong:
    //   • contents first — the slide is still linked, so the persistence listener projects
    //     the new contents INTO the nugget (§17.2.2), and every other deck is now pointed at
    //     bodies this deck owns privately;
    //   • unlink first — the slide is briefly this deck's while still pointing at the
    //     nugget's bodies, and the very next edit files the NUGGET's row under this deck's
    //     owner key, stealing it from everyone else.
    // Both windows are one `await` wide and both corrupt other people's decks. One action
    // closes them.
    linkBroken(
      state,
      action: PayloadAction<{ id: string; contents: SlideContents }>,
    ) {
      const slide = state.slides.find((s) => s.id === action.payload.id);
      if (!slide) return;
      slide.contents = action.payload.contents;
      delete slide.link;
    },
    // §17.2.2 The projection's receipt: the rev the storage layer just stamped, recorded on
    // the slide so the next open knows this copy is in step (§17.2.5). Guarded on the slide
    // still being linked to the SAME nugget — an await sat between the write and this
    // dispatch, and the user may have broken the link across it.
    linkRevSynced(
      state,
      action: PayloadAction<{ id: string; nuggetId: string; rev: number }>,
    ) {
      const slide = state.slides.find((s) => s.id === action.payload.id);
      if (slide?.link?.nuggetId === action.payload.nuggetId) {
        slide.link.rev = action.payload.rev;
      }
    },
    // §17.2.4/§17.2.5 The other direction: a nugget changed somewhere else — another window
    // on another deck, or the shelf between two opens — and every slide in THIS deck that
    // links it re-materializes. The mirrored fields are replaced wholesale from the payload
    // rather than merged, because "the same slide in five decks" means exactly that; the
    // fields §17.2.1 does not mirror (`id`, `archived`, `level`) are untouched here, which is
    // the whole content of that list.
    nuggetApplied(
      state,
      action: PayloadAction<{
        nuggetId: string;
        rev: number;
        fields: Partial<SlideData>;
      }>,
    ) {
      const { nuggetId, rev, fields } = action.payload;
      for (const slide of state.slides) {
        if (slide.link?.nuggetId !== nuggetId) continue;
        Object.assign(slide, fields);
        slide.link.rev = rev;
      }
    },
    // §24.3.1 The deck's logo — deck-level state, one reducer, and the persistence listener
    // does the rest; no new plumbing for a deck-level setting (the §10.3 transition shape).
    // `null` removes it. The blob's own lifecycle is NOT here: the caller writes the asset
    // before setting, and deletes the old one after replacing or removing (§24.0.4), because
    // IndexedDB is not something a reducer may touch.
    setLogo(state, action: PayloadAction<DeckLogo | null>) {
      if (!action.payload) {
        delete state.logo;
        return;
      }
      state.logo = {
        ...action.payload,
        sizePct: clampLogoSize(action.payload.sizePct),
      };
    },
    // §18.2.4 Archiving takes the group with it — `withGroup` archives the slide's evidence
    // children in the same action. Evidence is *defined* by its parent, so evidence left
    // behind is evidence attributed to a claim nobody is making any more. The caller asks
    // once and defaults to yes; the group itself is recomputed from adjacency here, at the
    // moment of the action, and recorded on neither slide.
    deleteSlide(
      state,
      action: PayloadAction<{ id: string; withGroup?: boolean }>,
    ) {
      const { id, withGroup } = action.payload;
      const ids = new Set([id]);
      if (withGroup) {
        for (const child of evidenceChildIds(state.slides, id)) ids.add(child);
      }
      let found = false;
      for (const slide of state.slides) {
        if (!ids.has(slide.id)) continue;
        slide.archived = true;
        found = true;
      }
      if (found) state.slides = normalizePyramid(state.slides);
    },
    // §18.2.4 Restore restores the group, with no prompt: the run of archived evidence
    // sitting directly behind an archived storyline slide is what a group archive left, and
    // bringing the parent back without them is the same orphaning in the other direction.
    // Re-derived from the array rather than resurrected from anything stored, so a restore
    // into a deck that has changed underneath takes back only what is still adjacent.
    restoreSlide(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      const slide = state.slides.find((s) => s.id === id);
      if (!slide) return;
      const group = new Set([id, ...archivedEvidenceAfter(state.slides, id)]);
      for (const s of state.slides) if (group.has(s.id)) s.archived = false;
      state.slides = normalizePyramid(state.slides);
    },
    // §20.0.1 The hard delete, as against `deleteSlide`'s soft one. Offered ONLY from the
    // archive, so the destructive action is always two steps behind a click — which is the
    // whole reason the archive exists.
    //
    // §20.0.3 This reducer is half of a pair and it is the SECOND half. The caller drops the
    // slide's grid bodies and image blobs first (`dropContentArtifacts`, once per content
    // window) while the slide is still in the document and its ids are still readable; after
    // this splice there is nothing left to read them off, and they are unreachable from the
    // deck record forever — the orphan rule (§3.5/§5.8) with no way back. Keeping the
    // artifact drop OUT of here is not an oversight: a reducer may not touch IndexedDB.
    purgeSlide(state, action: PayloadAction<{ id: string }>) {
      const at = state.slides.findIndex((s) => s.id === action.payload.id);
      if (at === -1) return;
      state.slides.splice(at, 1);
      // §18.1.3 A purge can strand the slides that followed it — deleting the only storyline
      // slide in front of an evidence block, say — so it repairs like every other mutation.
      state.slides = normalizePyramid(state.slides);
    },
    // §18.2.3 Moves the **group**, not the slide. This used to swap with the nearest
    // non-archived neighbour; that is still what it does for a slide with no children and no
    // parent, which is every slide in a flat deck. What changed is that a storyline slide now
    // travels with its evidence block — without which moving one silently re-parents its
    // evidence to whatever slide it landed behind — and that the backup suffix is a region a
    // slide cannot move into or out of by pressing an arrow. `movePyramid` states all three
    // rules and the outline's arrow buttons are disabled from the same ones.
    moveSlide(
      state,
      action: PayloadAction<{ id: string; direction: "up" | "down" }>,
    ) {
      const { id, direction } = action.payload;
      state.slides = movePyramid(state.slides, id, direction);
    },
  },
});

export const {
  hydrated,
  setTitle,
  setStyleSheet,
  setTransition,
  addSlide,
  setLogo,
  updateSlide,
  linkSet,
  linkBroken,
  linkRevSynced,
  nuggetApplied,
  setSlideLevel,
  deleteSlide,
  restoreSlide,
  purgeSlide,
  moveSlide,
} = documentSlice.actions;

export default documentSlice.reducer;
